<template>
    <div class="p-4">
        <div class="mb-3 flex items-center justify-between">
            <div>
                <!-- 返回按钮 -->
                <svg class="h-4 w-4 fill-current cursor-pointer" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg"
                    @click="backDir">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M7.46961 13.5303L8.53027 12.4696L3.0606 6.99994L8.53027 1.53027L7.46961 0.469614L0.939283 6.99994L7.46961 13.5303Z" />
                </svg>
            </div>
            <div>
                <!-- 刷新按钮 -->
                <div @click="handleSearchFile(true)" class="btn btn-sm btn-ghost cursor-pointer ml-2">
                    <svg-icon name="refresh" class="h-4 w-4 fill-current" />
                </div>
            </div>
        </div>
        <!-- 文件路径 -->
        <div class="flex items-center">
            <!-- 面包屑导航 -->
            <div v-if="!showPathInput"
                class="flex items-center flex-1 min-w-0 bg-base-100 border border-base-300 rounded-lg px-3 py-1 hover:border-primary/50 transition-all duration-200 group"
                @dblclick="togglePathInput">
                <div class="flex items-center flex-1 min-w-0">
                    <!-- 根目录图标 -->
                    <svg class="w-4 h-4 text-base-content/60 mr-2 flex-shrink-0 cursor-pointer" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24" @click="navigateToPath(rootPath)">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>

                    <!-- 面包屑路径 -->
                    <div ref="breadcrumbContainer"
                        class="flex items-center flex-1 min-w-0 overflow-x-auto scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-transparent">
                        <div v-for="(segment, index) in pathSegments" :key="index"
                            class="flex items-center flex-shrink-0">
                            <!-- 路径分隔符 -->
                            <svg v-if="index > 0"
                                class="breadcrumb-separator w-4 h-4 text-base-content/40 mx-1 flex-shrink-0" fill="none"
                                stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 5l7 7-7 7" />
                            </svg>

                            <!-- 路径段 -->
                            <span
                                class="breadcrumb-segment px-2 py-1 rounded-md text-sm cursor-pointer hover:bg-primary/10 hover:text-primary transition-all duration-200 flex-shrink-0 whitespace-nowrap"
                                :class="{ 'text-primary font-medium': index === pathSegments.length - 1 }"
                                :title="segment.name" @click="navigateToPath(segment.path)">
                                {{ segment.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 路径输入框 -->
            <input v-if="showPathInput" ref="pathInput" type="text" :placeholder="$tt('请输入文件夹路径：')" v-model="tempFilePath"
                @keyup.enter="confirmPathInput" @keyup.esc="cancelPathInput" @blur="confirmPathInput"
                class="input input-bordered input-sm flex-1 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200" />
        </div>
        <div class="flex items-center mt-2 relative">
            <!-- 搜索输入框容器 -->
            <div class="relative flex-1 mr-2" ref="searchContainer">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg class="w-4 h-4 text-base-content/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <input type="text" ref="searchInput"
                    class="input input-bordered input-sm w-full pl-10 pr-10 bg-base-100 border-base-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                    :placeholder="$tt('搜索文件、文件夹或使用过滤条件...')" v-model="searchWord" @input="handleSearchInput"
                    @keyup.enter="handleSearch" @click="toggleSearchResults" autocomplete="off" />

                <!-- 清空按钮 -->
                <div v-if="searchWord && searchWord.length > 0"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button @click="clearSearchInput"
                        class="p-1 rounded-full hover:bg-base-200 transition-colors duration-200 group">
                        <svg class="w-4 h-4 text-base-content/50 group-hover:text-base-content/80" fill="none"
                            stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- 搜索结果容器 -->
                <transition name="search-results">
                    <div class="absolute w-full mt-2 z-[12]"
                        v-if="showSearchResults && (searchSuggestions.length > 0 || !searchWord || searchWord.length === 0 || selectedFilterId)">
                        <div
                            class="search-results-container bg-base-100 border border-base-200 rounded-lg shadow-xl backdrop-blur-sm max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-transparent">

                            <!-- 预设过滤选项 -->
                            <div v-if="!searchWord || searchWord.length === 0 || selectedFilterId" class="p-2">
                                <div
                                    class="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider border-b border-base-200/50">
                                    <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                    {{ $tt('快速过滤') }}
                                </div>
                                <div class="py-1">
                                    <div v-for="(option, index) in filterOptions" :key="'filter' + index"
                                        class="suggestion-item flex items-center px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md transition-colors duration-150 group"
                                        @click.stop="selectFilterOption(option)">
                                        <div
                                            class="w-8 h-8 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-md flex items-center justify-center mr-3 group-hover:from-secondary/30 group-hover:to-accent/30 transition-all duration-150">
                                            <svg class="w-4 h-4 text-secondary" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-medium text-base-content truncate">{{ $tt(option.nameKey) }}
                                            </p>
                                            <p class="text-xs text-base-content/60">{{ $tt('按条件过滤文件') }}</p>
                                        </div>
                                        <svg class="w-4 h-4 text-base-content/40 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- 搜索建议结果 -->
                            <div v-if="searchSuggestions.length > 0 && searchWord && searchWord.length > 0 && !selectedFilterId"
                                class="p-2">
                                <div
                                    class="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider border-b border-base-200/50">
                                    <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    {{ $tt('搜索建议') }}
                                </div>
                                <div class="py-1">
                                    <div v-for="(suggestion, index) in searchSuggestions" :key="'suggest' + index"
                                        class="suggestion-item flex items-center px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md transition-colors duration-150 group"
                                        @click.stop="selectSuggestion(suggestion)">
                                        <div
                                            class="w-8 h-8 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-md flex items-center justify-center mr-3 group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-150">
                                            <svg v-if="suggestion.path.file_type === 'folder'"
                                                class="w-4 h-4 text-primary" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                            </svg>
                                            <svg v-else-if="suggestion.path.file_type === 'music'"
                                                class="w-4 h-4 text-primary" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                            </svg>
                                            <svg v-else class="w-4 h-4 text-primary" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-medium text-base-content truncate">{{ suggestion.name
                                                }}</p>
                                            <p class="text-xs text-base-content/60">{{ $tt('跳转到指定路径') }}</p>
                                        </div>
                                        <svg class="w-4 h-4 text-base-content/40 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- 无结果提示 -->
                            <div v-if="searchWord && searchWord.length > 0 && searchSuggestions.length === 0 && !selectedFilterId"
                                class="p-6 text-center">
                                <svg class="w-12 h-12 text-base-content/30 mx-auto mb-3" fill="none"
                                    stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <p class="text-base-content/60 text-sm">{{ $tt('未找到相关结果') }}</p>
                                <p class="text-base-content/40 text-xs mt-1">{{ $tt('尝试使用不同的关键词或过滤条件') }}</p>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- 排序按钮 -->
            <div class="dropdown dropdown-end">
                <label tabindex="0">
                    <div class="rounded-full cursor-pointer p-2">
                        <svg class="h-4 w-4 fill-current" viewBox="0 0 16 12" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M0.969727 3.96967L4.93939 8.70228e-06H5.75006V12H4.25006V2.81066L2.03039 5.03033L0.969727 3.96967ZM11.7501 0H10.2501L10.2501 12H11.0607L15.0304 8.03033L13.9697 6.96967L11.7501 9.18934L11.7501 0Z" />
                        </svg>
                    </div>
                </label>
                <ul tabindex="0"
                    class="mt-3 z-[1] p-2 shadow-xl menu menu-sm dropdown-content bg-base-100 rounded-lg border border-base-200/50 w-56">
                    <li><a href="javascript:;" @click="changeSorted('name')"
                            :class="{ 'text-primary font-semibold': getSortStatus('name') !== null }">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span class="flex-1">{{ $tt('名称') }}</span>
                            <!-- 排序状态指示器 -->
                            <svg v-if="getSortStatus('name') === 'asc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11L12 6L17 11M12 18V6" />
                            </svg>
                            <svg v-else-if="getSortStatus('name') === 'desc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13L12 18L7 13M12 6V18" />
                            </svg>
                        </a></li>
                    <li><a href="javascript:;" @click="changeSorted('update_time')"
                            :class="{ 'text-primary font-semibold': getSortStatus('update_time') !== null }">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="flex-1">{{ $tt('修改时间') }}</span>
                            <!-- 排序状态指示器 -->
                            <svg v-if="getSortStatus('update_time') === 'asc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11L12 6L17 11M12 18V6" />
                            </svg>
                            <svg v-else-if="getSortStatus('update_time') === 'desc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13L12 18L7 13M12 6V18" />
                            </svg>
                        </a></li>
                    <li><a href="javascript:;" @click="changeSorted('size')"
                            :class="{ 'text-primary font-semibold': getSortStatus('size') !== null }">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                            </svg>
                            <span class="flex-1">{{ $tt('大小') }}</span>
                            <!-- 排序状态指示器 -->
                            <svg v-if="getSortStatus('size') === 'asc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11L12 6L17 11M12 18V6" />
                            </svg>
                            <svg v-else-if="getSortStatus('size') === 'desc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13L12 18L7 13M12 6V18" />
                            </svg>
                        </a></li>
                    <li><a href="javascript:;" @click="changeSorted('state')"
                            :class="{ 'text-primary font-semibold': getSortStatus('state') !== null }">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span class="flex-1">{{ $tt('状态') }}</span>
                            <!-- 排序状态指示器 -->
                            <svg v-if="getSortStatus('state') === 'asc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11L12 6L17 11M12 18V6" />
                            </svg>
                            <svg v-else-if="getSortStatus('state') === 'desc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13L12 18L7 13M12 6V18" />
                            </svg>
                        </a></li>
                    <li><a href="javascript:;" @click="changeSorted('folder')"
                            :class="{ 'text-primary font-semibold': getSortStatus('folder') !== null }">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            <span class="flex-1">{{ $tt('目录') }}</span>
                            <!-- 排序状态指示器 -->
                            <svg v-if="getSortStatus('folder') === 'asc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11L12 6L17 11M12 18V6" />
                            </svg>
                            <svg v-else-if="getSortStatus('folder') === 'desc'" class="w-4 h-4 ml-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13L12 18L7 13M12 6V18" />
                            </svg>
                        </a></li>
                </ul>
            </div>
        </div>
        <transition name="slide-fade">
            <div class="tree-container mt-2" v-show="fadeShowDir" @mouseup="handleMouseUp">
                <RecycleScroller ref="scroller" class="scroller" :items="flattenedTreeList" :item-size="28"
                    :buffer="100" key-field="id" :min-item-size="28" :prerender="20" :dynamic="false" v-slot="{ item }">
                    <div class="tree-node" :style="{ paddingLeft: item.level * 12 + 'px' }" :key="item.id">
                        <div class="flex items-center justify-start">
                            <!-- 复选框 -->
                            <div v-if="item.icon !== 'icon-image'" class="checkbox-wrapper mr-2"
                                @mousedown.stop="handleMouseDown(item, $event)"
                                @mouseenter.stop="handleMouseEnter(item, $event)">
                                <input type="checkbox" :checked="item.checked" @change.stop="handleCheck(item)"
                                    class="checkbox checkbox-sm" />
                            </div>
                            <span v-else class="mr-7">
                            </span>
                            <!-- 文件/文件夹图标 -->
                            <span class="mr-2">
                                <svg-icon :name="item.icon" :width="18" :height="18" :class="'fill-neutral-500'" />
                            </span>

                            <!-- 节点标题 -->
                            <span :class="getNodeClass(item)" @click.stop="nodeClick(item)" :title="item.title"
                                class="flex-1 truncate pr-4">
                                {{ item.title }}
                            </span>

                            <!-- 文件夹数量标记 -->
                            <span v-if="!item.parent && item.children" class="btn btn-xs ml-1"
                                @click.stop="markFolder(item.name)" :title="$tt('将文件夹标记为已完成')">
                                {{ item.children.length }}
                            </span>
                        </div>
                    </div>
                </RecycleScroller>
            </div>
        </transition>
        <dialog ref="imageDialog" class="modal">
            <div class="modal-box relative">
                <div v-if="isImageLoading" class="flex justify-center items-center h-64">
                    <div class="loading loading-spinner loading-lg"></div>
                </div>
                <img v-else :src="imageUrl" :alt="$tt('图片预览')" class="w-full h-full object-contain">
                <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    @click="closeImageDialog">✕</button>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>{{ $tt('关闭') }}</button>
            </form>
        </dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import SvgIcon from '@/components/SvgIcon.vue'

export default {
    name: 'FileTree',
    components: {
        RecycleScroller,
        SvgIcon
    },
    data() {
        return {
            treeListOne: [],
            fadeShowDir: false,
            sortedField: localStorage.getItem('sortedField') ? JSON.parse(localStorage.getItem('sortedField')) : [],
            filterOptions: [
                { 'id': 'state:null', 'nameKey': '过滤: 未刮削' },
                { 'id': 'state:success', 'nameKey': '过滤: 成功' },
                { 'id': 'state:failed', 'nameKey': '过滤: 失败' }
            ],
            expandedNodes: new Set(),
            checkedNodesMap: new Map(),
            scrollPositions: {},
            lastPath: '',
            lastClickedNodeId: null,
            pathToLastClickedNode: {},
            imageUrl: '',
            isImageLoading: false,
            isMouseDown: false,
            startNodeId: null,
            lastHoveredNodeId: null,
            startNodeChecked: false,
            showSearchResults: false,
            searchSuggestions: [],
            searchInputTimer: null,
            selectedFilterId: null,
            // 面包屑相关
            showPathInput: false,
            tempFilePath: '',
            // rootPath: '/Users/macbookair/Music/my_music/music', // todo test
            rootPath: '/app/media',
            
            // 防抖和性能优化相关
            isOperationLocked: false,
            checkboxDebounceTimeout: null,
            stateUpdateQueue: []
        }
    },
    computed: {
        ...mapGetters(['geFullPath']),
        filePath: {
            get() {
                return this.$store.state.common.fullPath
            },
            set(value) {
                this.$store.commit('setFullPath', value)
                return value
            }
        },
        checkedIds: {
            get() {
                return this.$store.state.common.checkedIds
            },
            set(value) {
                this.$store.commit('setCheckedIds', value)
                return value
            }
        },
        checkedData: {
            get() {
                return this.$store.state.common.checkedData
            },
            set(value) {
                this.$store.commit('setCheckedData', value)
                return value
            }
        },
        searchWord: {
            get() {
                return this.$store.state.common.searchWord
            },
            set(value) {
                this.$store.commit('setSearchWord', value)
                return value
            }
        },
        // 面包屑路径段
        pathSegments() {
            if (!this.filePath) return []
            const segments = []

            // 判断 filePath 是否在 rootPath 前面（即 filePath 不是 rootPath 的子路径）
            if (!this.filePath.startsWith(this.rootPath)) {
                // 处理 filePath 比 rootPath 更靠前的情况
                // 例如 rootPath: /app/media, filePath: /app
                // 先分割 filePath
                const filePathParts = this.filePath.split('/').filter(part => part !== '')
                let currentPath = ''
                filePathParts.forEach((part, index) => {
                    currentPath += '/' + part
                    segments.push({
                        name: part,
                        path: currentPath
                    })
                })
                // // 添加根目录
                // segments.push({
                //     name: '根目录',
                //     path: this.rootPath
                // })
            } else {
                // 正常情况，filePath 在 rootPath 之下或等于 rootPath
                // 先添加根目录
                segments.push({
                    name: this.$tt('根目录'),
                    path: this.rootPath
                })
                // 处理 rootPath 之后的部分
                const path = this.filePath.replace(this.rootPath, '')
                const parts = path.split('/').filter(part => part !== '')
                let currentPath = this.rootPath
                parts.forEach((part, index) => {
                    // 保证路径拼接正确
                    if (!currentPath.endsWith('/')) {
                        currentPath += '/'
                    }
                    currentPath += part
                    segments.push({
                        name: part,
                        path: currentPath
                    })
                })
            }
            return segments
        },
        flattenedTreeList() {
            let flattened = []
            if (!this.treeListOne || !this.treeListOne.length) return flattened

            const processNode = (node, level = 0, parent = null) => {
                // 改进的ID生成策略：使用路径和名称生成稳定的ID
                if (!node.id) {
                    const parentPath = parent ? (parent.path || parent.name) : ''
                    const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name
                    // 使用路径哈希生成稳定的ID，避免时间戳导致的不稳定
                    node.id = `node_${this.generateStableId(nodePath)}_${level}`
                }

                const nodeData = {
                    ...node,
                    level,
                    parent,
                    path: parent ? `${parent.path || parent.name}/${node.name}` : node.name,
                    expanded: this.expandedNodes.has(node.id),
                    checked: this.checkedIds.includes(node.id)
                }

                flattened.push(nodeData)

                if (node.children && node.children.length && nodeData.expanded) {
                    node.children.forEach(child => {
                        processNode(child, level + 1, node)
                    })
                }
            }

            processNode(this.treeListOne[0], 0, null)

            // 添加搜索过滤逻辑
            if (this.searchWord) {
                // 如果是使用过滤选项，不进行前端过滤，因为后端已经过滤了
                if (this.selectedFilterId && this.selectedFilterId.startsWith('state:')) {
                    return flattened
                }
                // 如果是普通搜索或者直接输入的 state: 格式，进行前端过滤
                if (!this.searchWord.startsWith('state:')) {
                    // 按名称过滤
                    const searchLower = this.searchWord.toLowerCase()
                    return flattened.filter(node =>
                        node.name.toLowerCase().includes(searchLower)
                    )
                }
            }

            return flattened
        }
    },
    watch: {
        filePath: {
            handler(newPath, oldPath) {
                // 保存旧路径的滚动位置
                if (oldPath && this.$refs.scroller) {
                    this.scrollPositions[oldPath] = this.$refs.scroller.$el.scrollTop
                }
                this.lastPath = oldPath
                // 处理文件路径变化
                this.handleSearchFile()
                // 滚动面包屑到最右边
                this.scrollBreadcrumbToRight()
            }
        },
        pathSegments: {
            handler() {
                // 当面包屑路径段发生变化时，滚动到最右边
                this.scrollBreadcrumbToRight()
            },
            deep: true
        }
    },
    created() {
        const path = this.$route.query.path
        if (path) {
            this.filePath = path
            const target = this.$route.query.target || ''
            if (target === 'detail') {
                this.$router.replace({ path: '/detail', query: {} })
            } else {
                this.$router.replace({ path: '/', query: {} })
            }
        }
        this.handleSearchFile()
    },
    mounted() {
        // 添加全局点击事件监听器，用于点击外部区域关闭搜索面板
        document.addEventListener('click', this.handleDocumentClick)
    },
    beforeDestroy() {
        // 移除全局点击事件监听器
        document.removeEventListener('click', this.handleDocumentClick)
        // 清理拖拽相关的事件监听器
        document.removeEventListener('mouseup', this.handleGlobalMouseUp)
        document.removeEventListener('mouseleave', this.handleMouseLeave)
        // 清理防抖定时器
        if (this.checkboxDebounceTimeout) {
            clearTimeout(this.checkboxDebounceTimeout)
        }
        if (this.searchInputTimer) {
            clearTimeout(this.searchInputTimer)
        }
    },
    methods: {
        // 展开/折叠节点
        toggleNode(node) {
            if (!node.children || !node.children.length) return

            if (this.expandedNodes.has(node.id)) {
                this.expandedNodes.delete(node.id)
            } else {
                this.expandedNodes.add(node.id)
            }

            // 强制更新视图
            this.$nextTick(() => {
                this.treeListOne = [...this.treeListOne]
            })
        },

        // 获取节点样式类
        getNodeClass(node) {
            let classes = ['node-title']
            if (node.selected) classes.push('node-selected')

            if (node.state === 'success') {
                classes.push('text-slate-500')
            } else if (node.state === 'failed') {
                classes.push('text-red-500')
            } else {
                classes.push('text-current')
            }

            if (this.lastClickedNodeId === node.id) {
                classes.push('bg-gray-100')
            }

            classes.push('hover:underline')
            return classes.join(' ')
        },

        // 处理节点勾选
        handleCheck(node) {
            // 防抖保护：如果操作被锁定，直接返回
            if (this.isOperationLocked) {
                return
            }
            
            // 锁定操作，防止重复点击
            this.isOperationLocked = true
            
            // 清除之前的防抖定时器
            if (this.checkboxDebounceTimeout) {
                clearTimeout(this.checkboxDebounceTimeout)
            }
            
            const checked = !node.checked
            
            // 使用requestAnimationFrame确保DOM更新的时序
            this.$nextTick(() => {
                // 更新节点状态
                this.updateNodeCheckState(node, checked)
                
                // 原子性地同步到 store
                this.syncToStore()
                
                // 延迟解锁，防止过快的重复操作
                this.checkboxDebounceTimeout = setTimeout(() => {
                    this.isOperationLocked = false
                }, 100) // 100ms防抖
            })
        },
        
        // 原子性地将状态同步到store
        syncToStore() {
            // 状态一致性检查
            if (this.checkedIds.length !== this.checkedData.length) {
                console.warn('状态不一致，正在修复...', {
                    idsLength: this.checkedIds.length,
                    dataLength: this.checkedData.length
                })
                
                // 修复状态不一致：以checkedIds为准
                this.checkedData = this.checkedData.filter(item => 
                    this.checkedIds.includes(item.id)
                )
            }
            
            // 一次性提交到store，避免多次更新
            this.$store.commit('setCheckedIds', [...this.checkedIds])
            this.$store.commit('setCheckedData', [...this.checkedData])
        },

        // 生成稳定的ID哈希
        generateStableId(str) {
            // 简单的哈希函数，确保相同输入产生相同输出
            let hash = 0
            if (!str || str.length === 0) return hash
            for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i)
                hash = ((hash << 5) - hash) + char
                hash = hash & hash // 转换为32位整数
            }
            return Math.abs(hash).toString(36)
        },

        // 更新节点的勾选状态
        updateNodeCheckState(node, checked) {
            if (!node) return

            // 只更新当前节点的勾选状态
            this.$set(node, 'checked', checked)

            // 如果是文件夹，更新其直接子节点
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => {
                    this.$set(child, 'checked', checked)
                    // 更新选中节点的ID列表
                    if (checked) {
                        if (!this.checkedIds.includes(child.id)) {
                            this.checkedIds.push(child.id)
                            this.checkedData.push(child)
                        }
                    } else {
                        // 修复：使用正确的方式同步删除checkedIds和checkedData
                        const idIndex = this.checkedIds.indexOf(child.id)
                        const dataIndex = this.checkedData.findIndex(item => item.id === child.id)
                        if (idIndex > -1) {
                            this.checkedIds.splice(idIndex, 1)
                        }
                        if (dataIndex > -1) {
                            this.checkedData.splice(dataIndex, 1)
                        }
                    }
                })
            }
            // 更新当前节点的ID
            if (checked) {
                if (!this.checkedIds.includes(node.id)) {
                    this.checkedIds.push(node.id)
                    this.checkedData.push(node)
                }
            } else {
                // 修复：使用正确的方式同步删除checkedIds和checkedData
                const idIndex = this.checkedIds.indexOf(node.id)
                const dataIndex = this.checkedData.findIndex(item => item.id === node.id)
                if (idIndex > -1) {
                    this.checkedIds.splice(idIndex, 1)
                }
                if (dataIndex > -1) {
                    this.checkedData.splice(dataIndex, 1)
                }
            }
        },

        // 处理节点点击
        nodeClick(node) {
            this.lastClickedNodeId = node.id
            this.pathToLastClickedNode[this.filePath] = node.id

            if (node.icon === 'icon-folder') {
                if (node.children && node.children.length > 0) {
                    this.toggleNode(node)
                } else {
                    // 处理空文件夹的点击
                    if (this.filePath.endsWith('/')) {
                        this.filePath = this.filePath + node.name
                    } else {
                        this.filePath = this.filePath + '/' + node.name
                    }
                }
            } else if (node.icon === 'icon-image') {
                this.imageUrl = ''
                this.isImageLoading = true
                this.$refs.imageDialog.showModal()
                // 调用后端接口获取图片
                let imagePath = ''
                if (this.filePath.endsWith('/')) {
                    imagePath = this.filePath + node.name
                } else {
                    imagePath = this.filePath + '/' + node.name
                }
                this.$api.Task.getImage({ 'file_path': imagePath })
                    .then(response => {
                        if (response.result) {
                            this.imageUrl = `data:image/jpeg;base64,${response.data.image_data}`
                        } else {
                            this.$myMsg.notify({
                                title: this.$tt('图片加载失败'),
                                content: response.message,
                                type: 'error',
                                time: 3000,
                            })
                        }
                    })
                    .catch(error => {
                        this.$myMsg.notify({
                            title: this.$tt('图片加载失败'),
                            content: error.message,
                            type: 'error',
                            time: 3000,
                        })
                    })
                    .finally(() => {
                        this.isImageLoading = false
                    })
            } else {
                // 处理文件点击
                this.isShowEdit = true
                this.showId3List = this.checkedIds.length > 0
                this.musicInfo = this.baseMusicInfo
                this.fileName = node.name
                if (this.filePath.endsWith('/')) {
                    this.fullPath = this.filePath + node.name
                } else {
                    this.fullPath = this.filePath + '/' + node.name
                }
                this.$parent.showEditPageDrawer(this.filePath, node.name)
            }
        },

        // 返回上一级
        backDir() {
            if (this.$refs.scroller) {
                this.scrollPositions[this.filePath] = this.$refs.scroller.$el.scrollTop
            }

            // 返回上一级目录
            const previousPath = this.backPath(this.filePath)

            // 恢复上一级目录中最后点击的节点
            this.lastClickedNodeId = this.pathToLastClickedNode[previousPath] || null

            this.filePath = previousPath
        },

        backPath(path) {
            if (path === '/') {
                return '/app'
            } else if (path === '/app') {
                return '/app'
            } else if (path === '/app/') {
                return '/app'
            }
            const regex = /\/([^\/]+)\/?$/
            const match = regex.exec(path)

            if (match) {
                const parentPath = path.slice(0, match.index)
                return parentPath
            }
            return path
        },

        // 文件目录
        handleSearchFile(refresh = false) {
            // 确保 refresh 是布尔值
            const isRefresh = typeof refresh === 'boolean' ? refresh : Boolean(refresh === true)

            this.fadeShowDir = false
            this.checkedData = []
            this.checkedIds = []
            this.checkedNodesMap.clear()
            this.$parent.deleteFileID3List()

            this.$api.Task.fileList(
                {
                    'file_path': this.filePath,
                    sorted_fields: this.sortedField,
                    'search_word': this.searchWord,
                    'refresh': isRefresh
                }
            ).then((res) => {
                if (res.result) {
                    this.treeListOne = res.data.file_list_data
                    this.expandAllNodes(this.treeListOne)

                    this.$nextTick(() => {
                        this.fadeShowDir = true
                        if (this.$refs.scroller) {
                            const savedPosition = this.scrollPositions[this.filePath]
                            if (savedPosition !== undefined) {
                                this.$refs.scroller.$el.scrollTop = savedPosition
                            } else {
                                this.$refs.scroller.$el.scrollTop = 0
                            }
                        }
                    })

                    if (res.data.file_name !== '') {
                        let value = this.filePath.replace(res.data.file_name, '')
                        if (value.endsWith('/')) {
                            value = value.substring(0, value.length - 1)
                        }
                        this.$nextTick(() => {
                            this.filePath = value
                        })
                        this.$nextTick(() => {
                            this.nodeClick({ 'name': res.data.file_name, 'icon': 'icon-file' })
                        })
                    }
                } else {
                    if (res.message.status === 401) {
                        return
                    }
                    this.$myMsg.notify({
                        title: this.$tt('查询失败！'),
                        content: res.message || res.message.data.message,
                        type: 'error',
                        time: 5000,
                    })
                }
            })
            this.$parent.fetchFileID3FolderList('all', isRefresh)
        },

        // 带过滤条件的文件目录搜索
        handleSearchFileWithFilter(filterId, refresh = false) {
            // 确保 refresh 是布尔值
            const isRefresh = typeof refresh === 'boolean' ? refresh : Boolean(refresh === true)

            this.fadeShowDir = false
            this.checkedData = []
            this.checkedIds = []
            this.checkedNodesMap.clear()
            this.$parent.deleteFileID3List()

            this.$api.Task.fileList(
                {
                    'file_path': this.filePath,
                    sorted_fields: this.sortedField,
                    'search_word': filterId,
                    'refresh': isRefresh
                }
            ).then((res) => {
                if (res.result) {
                    this.treeListOne = res.data.file_list_data
                    this.expandAllNodes(this.treeListOne)

                    this.$nextTick(() => {
                        this.fadeShowDir = true
                        if (this.$refs.scroller) {
                            const savedPosition = this.scrollPositions[this.filePath]
                            if (savedPosition !== undefined) {
                                this.$refs.scroller.$el.scrollTop = savedPosition
                            } else {
                                this.$refs.scroller.$el.scrollTop = 0
                            }
                        }
                    })

                    if (res.data.file_name !== '') {
                        let value = this.filePath.replace(res.data.file_name, '')
                        if (value.endsWith('/')) {
                            value = value.substring(0, value.length - 1)
                        }
                        this.$nextTick(() => {
                            this.filePath = value
                        })
                        this.$nextTick(() => {
                            this.nodeClick({ 'name': res.data.file_name, 'icon': 'icon-file' })
                        })
                    }
                } else {
                    if (res.message.status === 401) {
                        return
                    }
                    this.$myMsg.notify({
                        title: this.$tt('查询失败！'),
                        content: res.message || res.message.data.message,
                        type: 'error',
                        time: 5000,
                    })
                }
            })
            this.$parent.fetchFileID3FolderList('all', isRefresh)
        },

        // 过滤搜索
        handleSearch() {
            console.log(this.searchWord)
            if (this.searchWord === '') {
                this.selectedFilterId = null
                this.handleSearchFile()
                return
            }

            // 如果是选择的过滤选项，直接使用过滤 ID 调用搜索
            if (this.selectedFilterId && this.selectedFilterId.startsWith('state:')) {
                this.handleSearchFileWithFilter(this.selectedFilterId)
            } else if (this.searchWord.startsWith('state:')) {
                this.handleSearchFile()
            } else if (this.searchWord.startsWith('path:')) {
                this.filePath = this.searchWord.replace('path:', '')
                this.searchWord = ''
                this.selectedFilterId = null
            } else {
                // 清除过滤 ID，因为这是普通搜索
                this.selectedFilterId = null
                this.$api.Task.searchIndex({ 'search_text': this.searchWord }).then((res) => {
                    if (res.result) {
                        console.log(res.data)
                        this.searchSuggestions = res.data.map(item => ({
                            id: `path:${item.path || item}`,
                            name: item.name || item,
                            path: item.path || item,
                            file_type: item.file_type || 'music'
                        }))
                    }
                })
            }
            this.showSearchResults = false
        },

        // 搜索输入处理
        handleSearchInput() {
            // 清除之前的定时器
            if (this.searchInputTimer) {
                clearTimeout(this.searchInputTimer)
            }

            // 如果搜索框为空，清除搜索建议并显示预设过滤选项
            if (!this.searchWord || this.searchWord.length === 0) {
                this.searchSuggestions = []
                this.selectedFilterId = null
                this.showSearchResults = true
                return
            }

            // 如果用户手动编辑搜索框，清除选中的过滤 ID
            this.selectedFilterId = null

            // 延迟执行搜索，避免频繁请求
            this.searchInputTimer = setTimeout(() => {
                if (this.searchWord && this.searchWord.length > 0) {
                    if (!this.searchWord.startsWith('state:') && !this.searchWord.startsWith('path:')) {
                        // 获取搜索建议
                        this.$api.Task.searchIndex({ 'search_text': this.searchWord }).then((res) => {
                            if (res.result && res.data) {
                                this.searchSuggestions = res.data.map(item => ({
                                    id: `path:${item.path || item}`,
                                    name: item.name || item,
                                    path: item.path || item
                                }))
                            } else {
                                this.searchSuggestions = []
                            }
                        }).catch(() => {
                            this.searchSuggestions = []
                        })
                    }
                }
            }, 300)
        },

        // 切换搜索结果显示状态
        toggleSearchResults() {
            this.showSearchResults = !this.showSearchResults
            // 如果显示且没有搜索词或者是已选择的过滤选项，确保预设过滤选项能够显示
            if (this.showSearchResults && ((!this.searchWord || this.searchWord.length === 0) || this.selectedFilterId)) {
                this.searchSuggestions = []
            }
        },

        // 显示搜索结果
        showSearchResultsPanel() {
            this.showSearchResults = true
            // 如果没有搜索词，确保预设过滤选项能够显示
            if (!this.searchWord || this.searchWord.length === 0) {
                this.searchSuggestions = []
            }
        },

        // 隐藏搜索结果
        hideSearchResults() {
            this.showSearchResults = false
        },

        // 选择过滤选项
        selectFilterOption(option) {
            // 在输入框中显示友好的名称，但保存原始 ID 用于搜索
            this.searchWord = this.$tt(option.nameKey)
            this.selectedFilterId = option.id
            this.showSearchResults = false
            this.handleSearch()
        },

        // 选择搜索建议
        selectSuggestion(suggestion) {
            if (suggestion.path) {
                this.filePath = suggestion.path.id.replace('path:', '')
                this.searchWord = suggestion.path.name
                this.selectedFilterId = null
                this.showSearchResults = false
                this.handleSearchFile()
            }
        },

        // 清空搜索输入框
        clearSearchInput() {
            this.searchWord = ''
            this.searchSuggestions = []
            this.selectedFilterId = null
            this.showSearchResults = true
            this.$refs.searchInput.focus()
            // 清空搜索后刷新文件树
            this.handleSearchFile()
        },

        // 更改排序条件 - 支持正序、倒序、不排序三种状态
        changeSorted(element) {
            const positiveField = element
            const negativeField = '-' + element
            const positiveIndex = this.sortedField.indexOf(positiveField)
            const negativeIndex = this.sortedField.indexOf(negativeField)

            // 移除当前字段的所有状态
            if (positiveIndex > -1) {
                this.sortedField.splice(positiveIndex, 1)
            }
            if (negativeIndex > -1) {
                this.sortedField.splice(negativeIndex, 1)
            }

            // 状态切换：无排序 -> 正序 -> 倒序 -> 无排序
            if (positiveIndex === -1 && negativeIndex === -1) {
                // 当前无排序，设置为正序
                this.sortedField.push(positiveField)
            } else if (positiveIndex > -1) {
                // 当前是正序，设置为倒序
                this.sortedField.push(negativeField)
            }
            // 如果当前是倒序，则不添加任何字段（回到无排序状态）

            localStorage.setItem('sortedField', JSON.stringify(this.sortedField))
            this.$store.commit('setSortedField', this.sortedField)
            this.handleSearchFile()
        },

        // 获取排序状态 - 返回 'asc'(正序), 'desc'(倒序), 或 null(无排序)
        getSortStatus(element) {
            const positiveField = element
            const negativeField = '-' + element
            
            if (this.sortedField.includes(positiveField)) {
                return 'asc'
            } else if (this.sortedField.includes(negativeField)) {
                return 'desc'
            }
            return null
        },

        // 标记文件夹为已处理
        markFolder() {
            this.$api.Task.markFolderState({ 'full_path': this.filePath }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('标记成功！'),
                        content: '',
                        type: 'success',
                        time: 2000,
                    })
                    this.handleSearchFile()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('创建失败！'),
                        content: res.messagee ? res.message: this.$tt('创建失败'),
                        type: 'error',
                        time: 5000,
                    })
                }
            })
        },

        // 展开所有节点 - 由于只有两层，这个方法可以简化
        expandAllNodes(nodes) {
            if (!nodes || !nodes.length) return

            const rootNode = nodes[0]
            if (!rootNode.id) {
                rootNode.id = `${rootNode.name}-${Date.now()}`
            }
            this.expandedNodes.add(rootNode.id)

            // 强制更新视图
            this.$nextTick(() => {
                this.treeListOne = JSON.parse(JSON.stringify(this.treeListOne))
            })
        },

        closeImageDialog() {
            this.$refs.imageDialog.close()
        },

        handleImageError() {
            this.$myMsg.notify({
                title: this.$tt('图片加载失败'),
                content: this.$tt('无法加载图片'),
                type: 'error',
                time: 3000,
            })
        },

        handleMouseDown(node, event) {
            if (event.button !== 0) return // 只处理左键点击
            if (node.icon === 'icon-image') return // 不处理图片文件
            if (this.isOperationLocked) return // 如果操作被锁定，不处理拖拽

            event.preventDefault() // 阻止默认行为
            event.stopPropagation() // 阻止事件冒泡
            
            // 重置拖拽状态，确保清洁的开始
            this.resetDragState()
            
            this.isMouseDown = true
            this.startNodeId = node.id
            this.lastHoveredNodeId = node.id
            this.startNodeChecked = node.checked // 记录起始节点的选中状态
            
            // 添加全局监听器处理边界情况
            document.addEventListener('mouseup', this.handleGlobalMouseUp)
            document.addEventListener('mouseleave', this.handleMouseLeave)
            
            this.handleCheck(node)
        },

        handleMouseEnter(node, event) {
            if (!this.isMouseDown || !this.startNodeId) return
            if (node.icon === 'icon-image') return // 不处理图片文件
            if (this.isOperationLocked) return // 如果操作被锁定，不处理

            event.preventDefault() // 阻止默认行为
            event.stopPropagation() // 阻止事件冒泡

            // 防止重复处理同一个节点
            if (this.lastHoveredNodeId === node.id) return
            this.lastHoveredNodeId = node.id

            try {
                // 获取所有节点（增加边界检查）
                const allNodes = this.flattenedTreeList
                if (!allNodes || allNodes.length === 0) {
                    console.warn('flattenedTreeList为空，停止拖拽选择')
                    this.resetDragState()
                    return
                }
                
                const startIndex = allNodes.findIndex(n => n.id === this.startNodeId)
                const currentIndex = allNodes.findIndex(n => n.id === node.id)

                // 增强边界检查
                if (startIndex === -1 || currentIndex === -1) {
                    console.warn('无法找到节点索引', { startNodeId: this.startNodeId, currentNodeId: node.id })
                    return
                }

                // 限制选择范围，避免过大的选择导致性能问题
                const maxSelectionRange = 100 // 最大选择100个节点
                const selectionRange = Math.abs(currentIndex - startIndex)
                if (selectionRange > maxSelectionRange) {
                    console.warn('选择范围过大，停止拖拽选择', { range: selectionRange })
                    return
                }

                // 确定选择范围
                const start = Math.min(startIndex, currentIndex)
                const end = Math.max(startIndex, currentIndex)

                // 使用requestAnimationFrame优化性能
                requestAnimationFrame(() => {
                    // 批量更新选中状态
                    for (let i = start; i <= end; i++) {
                        const currentNode = allNodes[i]
                        if (currentNode && currentNode.icon !== 'icon-image') {
                            // 根据起始节点的选中状态来决定是选中还是取消选中
                            this.updateNodeCheckState(currentNode, !this.startNodeChecked)
                        }
                    }
                    
                    // 同步状态到store
                    this.syncToStore()
                })
            } catch (error) {
                console.error('拖拽选择过程中发生错误:', error)
                this.resetDragState()
            }
        },

        handleMouseUp(event) {
            if (event) {
                event.preventDefault() // 阻止默认行为
                event.stopPropagation() // 阻止事件冒泡
            }
            
            this.resetDragState()
        },
        
        // 全局鼠标抬起事件处理（处理鼠标移出组件区域的情况）
        handleGlobalMouseUp() {
            this.resetDragState()
        },
        
        // 鼠标离开事件处理
        handleMouseLeave(event) {
            // 如果鼠标离开了整个文档区域，重置拖拽状态
            if (event.relatedTarget === null) {
                this.resetDragState()
            }
        },
        
        // 重置拖拽状态的统一方法
        resetDragState() {
            this.isMouseDown = false
            this.startNodeId = null
            this.lastHoveredNodeId = null
            this.startNodeChecked = false
            
            // 移除全局监听器
            document.removeEventListener('mouseup', this.handleGlobalMouseUp)
            document.removeEventListener('mouseleave', this.handleMouseLeave)
        },

        // 处理文档点击事件，点击搜索容器外部时关闭搜索面板
        handleDocumentClick(event) {
            // 如果点击的是搜索容器外部，则关闭搜索面板
            if (this.$refs.searchContainer && !this.$refs.searchContainer.contains(event.target)) {
                this.hideSearchResults()
            }
        },

        // 面包屑相关方法
        // 切换路径输入框显示状态
        togglePathInput() {
            this.showPathInput = true
            this.tempFilePath = this.filePath
            this.$nextTick(() => {
                if (this.$refs.pathInput) {
                    this.$refs.pathInput.focus()
                    this.$refs.pathInput.select()
                }
            })
        },

        // 确认路径输入
        confirmPathInput() {
            if (this.tempFilePath.trim() !== '' && this.tempFilePath !== this.filePath) {
                this.filePath = this.tempFilePath.trim()
                // 路径变化后滚动面包屑到最右边
                this.scrollBreadcrumbToRight()
            }
            this.showPathInput = false
            this.tempFilePath = ''
        },

        // 取消路径输入
        cancelPathInput() {
            this.showPathInput = false
            this.tempFilePath = ''
        },

        // 导航到指定路径
        navigateToPath(path) {
            console.log('navigateToPath', path)
            if (path !== this.filePath) {
                this.filePath = path
            }
        },

        // 滚动面包屑到最右边
        scrollBreadcrumbToRight() {
            this.$nextTick(() => {
                if (this.$refs.breadcrumbContainer) {
                    const container = this.$refs.breadcrumbContainer
                    const maxScrollLeft = container.scrollWidth - container.clientWidth

                    // 如果内容超出容器宽度，才需要滚动
                    if (maxScrollLeft > 0) {
                        // 使用平滑滚动
                        container.scrollTo({
                            left: maxScrollLeft,
                            behavior: 'smooth'
                        })
                    }
                }
            })
        },
    }
}
</script>

<style>
.scroller {
    height: 100%;
    overflow-y: auto;
    background-color: transparent;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    /* 添加滚动条样式 */
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
}

/* Webkit浏览器的滚动条样式 */
.scroller::-webkit-scrollbar {
    width: 6px;
}

.scroller::-webkit-scrollbar-track {
    background: transparent;
}

.scroller::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 3px;
}

.scroller::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.5);
}

/* 确保父容器有明确的高度 */
.tree-container {
    height: calc(100vh - 250px);
    overflow: hidden;
}

.tree-node {
    cursor: pointer;
    transition: background-color 0.2s;
    user-select: none;
    height: 24px;
    line-height: 20px;
    box-sizing: border-box;
    font-size: 12px;
}

.tree-node:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.node-title {
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    padding: 2px 4px;
    border-radius: 4px;
}

.node-title.bg-gray-100 {
    background-color: rgba(0, 0, 0, 0.05);
    transition: background-color 0.3s ease;
}

.node-selected {
    color: #2d8cf0;
}

.isSelected {
    color: #2d8cf0;
    font-weight: bold;
}

.checkbox {
    cursor: pointer;
    transform: scale(0.8);
    margin-top: 1px;
}

.checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
}

.checkbox-wrapper .checkbox {
    pointer-events: none;
}

/* 搜索结果容器动画 */
.search-results-enter-active,
.search-results-leave-active {
    transition: all 0.3s ease;
    transform-origin: top;
}

.search-results-enter,
.search-results-leave-to {
    opacity: 0;
    transform: scaleY(0.8) translateY(-10px);
}

/* 搜索建议项的动画 */
.suggestion-item {
    transition: all 0.15s ease;
}

.suggestion-item:hover {
    transform: translateX(4px);
}

/* 滚动条样式优化 */
.scrollbar-thin::-webkit-scrollbar {
    width: 4px;
    height: 4px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 2px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.7);
}

/* 输入框聚焦效果增强 */
/* .input:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-color: #3b82f6;
} */

/* 搜索结果容器阴影增强 */
.search-results-container {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    backdrop-filter: blur(8px);
}

/* 响应式设计 */
@media (max-width: 768px) {
    .search-results-container {
        position: fixed;
        left: 1rem;
        right: 1rem;
        width: auto;
    }
}

/* 面包屑样式 */
.breadcrumb-container {
    transition: all 0.2s ease;
}

.breadcrumb-container:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.breadcrumb-segment {
    transition: all 0.15s ease;
    position: relative;
}

.breadcrumb-segment:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.breadcrumb-separator {
    transition: color 0.2s ease;
}

/* 路径输入动画 */
.path-input-enter-active,
.path-input-leave-active {
    transition: all 0.3s ease;
}

.path-input-enter,
.path-input-leave-to {
    opacity: 0;
    transform: scaleX(0.8);
}
</style>
